import { useEffect, useMemo, useState } from 'react';
import { getBusinessMessages, getTemplatesList, getWhatsappAccount, getTemplate, sendTemplateMessage } from "../../../api/login.service";
import { getBusinessLeads, getLeadPipeline } from "../../../api/leads.service";
import { useAuth } from "../../../authContext";
import MediaSelectionPopup from "../../../components/MediaSelectionPopup";
import LoadingComponent from "../../../components/LoadingComponent";
import { BusinessSetup, businessId, leadState, loadSetup } from "../inbox/businessSetup";
import { ChatRow, leadsToChats, loadAccount } from "../inbox/chats";

interface MarketingTemplate {
  id: string;
  name: string;
  category: string;
  language: string;
  status: string;
  content: string;
  components?: any[];
}

interface PhoneNumber {
  id: string;
  phoneNumber: string;
  phoneNumberId: string;
  businessName?: string;
  status?: string;
}

interface BulkSendPayload {
  templateId: string;
  templateName: string;
  phoneNumbers: string[];
  businessId?: string;
  variables?: Record<string, string>;
  components?: any[];
  scheduledTime?: string;
}

// Generate UUID for flow_token
function languageCode(raw: unknown): string {
  const value = typeof raw === "string" ? raw : String((raw as { code?: string } | null)?.code || "");
  if (!value) return "en_US";
  if (value.includes("_")) return value;
  return `${value}_US`;
}

function toggleId(current: string[], id: string): string[] {
  return current.includes(id) ? current.filter((item) => item !== id) : current.concat(id);
}

function leadWhatsapp(setup: BusinessSetup, chat: ChatRow): string {
  return (leadState(setup, chat.key).whatsappNumber || chat.customerNumber || "").trim();
}

function toE164(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 8) return "";
  return `+${digits}`;
}

function phoneKey(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length >= 10) return digits.slice(-10);
  return digits;
}

function replyLabel(at: string): string {
  if (!at) return "No reply yet";
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return "No reply yet";
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Build WhatsApp components payload with flow logic
function buildWAComponentsForMarketing(variables: Record<string, string>, template: MarketingTemplate): any[] {
  const components: any[] = [];
  
  // Add image header if template has one OR custom header URL is provided
  const hasImageHeader = template.components?.some((c: any) => c.type === "HEADER" && c.format === "IMAGE");
  if (hasImageHeader && (variables['header_url'] || template.components?.find((c: any) => c.type === "HEADER" && c.format === "IMAGE")?.example?.header_handle?.[0])) {
    components.push({
      type: "header",
      parameters: [
        {
          type: "image",
          image: {
            link: variables['header_url'] || template.components.find((c: any) => c.type === "HEADER" && c.format === "IMAGE")?.example?.header_handle?.[0]
          }
        }
      ]
    });
  }
  
  // Extract template variables from content
  const variableKeys = template.content ? (template.content.match(/\{\{([^}]+)\}\}/g) || []).map(v => v.replace(/\{\{|\}\}/g, '')) : [];
  
  // Add body parameters if there are variables
  if (variableKeys.length > 0) {
    const parameters = variableKeys.map((k) => ({
      type: "text",
      text: String(variables[k] ?? ""),
    }));
    components.push({ type: "body", parameters });
  }
  
  // Check if template has FLOW buttons and add them to components
  const buttonsComponent = template.components?.find((c: any) => c.type === "BUTTONS");
  
  if (buttonsComponent && Array.isArray(buttonsComponent.buttons) && buttonsComponent.buttons.length > 0) {
    // Find FLOW buttons specifically
    const flowButtons = buttonsComponent.buttons.filter((button: any) => button && button.type === "FLOW");
    
    // Only add flow button component if there are actual FLOW buttons
    if (flowButtons.length > 0) {
      flowButtons.forEach((button: any, flowIndex: number) => {
        // Find the original index of this FLOW button in the buttons array
        const originalIndex = buttonsComponent.buttons.findIndex((b: any) => b && b.type === "FLOW" && b === button);
        
        // Generate flow_token UUID for this flow button
        const flowToken = generateUUID();
        
        components.push({
          type: "button",
          sub_type: "flow",
          index: String(originalIndex >= 0 ? originalIndex : flowIndex),
          parameters: [
            {
              type: "action",
              action: {
                flow_token: flowToken
              }
            }
          ]
        });
      });
    }
  }
  
  return components;
}

// Function to fetch full template details
async function fetchFullTemplateDetails(templateId: string): Promise<MarketingTemplate> {
  try {
    console.log('Fetching full template details for:', templateId);
    
    const template = await getTemplate(templateId);
    console.log('Full template response:', template);
    
    // Extract body text from components
    const body = Array.isArray(template?.components)
      ? template.components.find((c: any) => c.type === "BODY")?.text
      : template?.content ?? template?.body ?? "";
    
    // Check if template has image header
    const imageHeader = Array.isArray(template?.components)
      ? template.components.find((c: any) => c.type === "HEADER" && c.format === "IMAGE")
      : null;
    
    const result = {
      id: String(template?.id ?? template?._id ?? templateId),
      name: String(template?.name ?? template?.templateName ?? ""),
      language: template?.language ?? template?.lang,
      category: template?.category ?? template?.type,
      status: String(template?.status ?? template?.state ?? "Unknown"),
      content: String(body ?? ""),
      components: template?.components || [],
    };
    
    console.log('Processed template result:', result);
    return result;
  } catch (error) {
    console.error('Error fetching template details:', error);
    throw error;
  }
}

export default function MarketingSend() {
  const { displayPhoneNumber } = useAuth();
  
  // Get wabaId and phoneNumberId from session storage
  const wabaId = sessionStorage.getItem('wabaId');
  const phoneNumberId = sessionStorage.getItem('phoneNumberId');
  
  console.log('MarketingSend: wabaId from session storage:', wabaId);
  console.log('MarketingSend: phoneNumberId from session storage:', phoneNumberId);
  
  // State for templates
  const [templates, setTemplates] = useState<MarketingTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<MarketingTemplate | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [templatesLoading, setTemplatesLoading] = useState(true);
  
  // State for phone numbers
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<string[]>([]);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = useState(true);
  
  // State for phone groups
  const [phoneGroups, setPhoneGroups] = useState<any[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [phoneGroupsLoading, setPhoneGroupsLoading] = useState(false);
  const [leadRows, setLeadRows] = useState<ChatRow[]>([]);
  const [leadSetup, setLeadSetup] = useState<BusinessSetup>(() => loadSetup());
  const [leadsLoading, setLeadsLoading] = useState(true);
  const [leadQuery, setLeadQuery] = useState("");
  const [stageIds, setStageIds] = useState<string[]>([]);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [fieldFilters, setFieldFilters] = useState<Record<string, string[]>>({});
  const [replyAt, setReplyAt] = useState<Record<string, string>>({});
  const [pickedPhones, setPickedPhones] = useState<string[]>([]);
  
  // State for selected group and its phone numbers
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [groupPhoneNumbers, setGroupPhoneNumbers] = useState<any[]>([]);
  const [selectedGroupPhoneNumbers, setSelectedGroupPhoneNumbers] = useState<string[]>([]);
  const [groupPhoneNumbersLoading, setGroupPhoneNumbersLoading] = useState(false);
  const [imageVersion, setImageVersion] = useState(0); // Version counter to force image reloads
  
  // State for sending
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // State for form
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [scheduledTime, setScheduledTime] = useState<string>('');
  const [scheduleMode, setScheduleMode] = useState<'now' | 'later'>('now');
  const [recipientMode, setRecipientMode] = useState<'groups'>('groups');
  
  // State for media selection popup
  const [showMediaPopup, setShowMediaPopup] = useState(false);
  
  // State for group selection popup
  const [showGroupPopup, setShowGroupPopup] = useState(false);

  // State for collapsing recipients section
  const [recipientsCollapsed, setRecipientsCollapsed] = useState(false);
  // State for side collapse (hiding right panel)
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);

  // Handle media selection
  const handleMediaSelect = (url: string) => {
    setVariables(prev => ({ ...prev, 'header_url': url }));
    setShowMediaPopup(false);
  };

  // Fetch approved templates
  const fetchApprovedTemplates = async () => {
    setTemplatesLoading(true);
    try {
      console.log('Fetching templates for wabaId:', wabaId);
      
      // Fetch templates from the API
      const data = await getTemplatesList(wabaId);
      console.log('Raw API response:', data);
      
      // Filter only approved templates (no category filter)
      const approvedTemplates = (data.data || []).filter((template: any) => 
        template.status === 'APPROVED'
      );
      
      console.log('Approved templates found:', approvedTemplates);
      console.log('Total templates in response:', data.data?.length || 0);
      
      // If no approved templates found, show all templates for debugging
      if (approvedTemplates.length === 0 && data.data && data.data.length > 0) {
        console.log('No approved templates found, showing all templates:', data.data);
        setTemplates(data.data);
      } else {
        setTemplates(approvedTemplates);
      }
      
      // Don't auto-select - let user choose from dropdown
      
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      setError(err?.message || 'Failed to fetch approved templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Fetch phone numbers
  const fetchPhoneNumbers = async () => {
    setPhoneNumbersLoading(true);
    try {
      const phoneNumber = sessionStorage.getItem('display_phone_number') || localStorage.getItem('display_phone_number');
      if (!phoneNumber) {
        throw new Error('No phone number found. Please log in again.');
      }

      const accounts = await getWhatsappAccount(phoneNumber);
      
      // Transform accounts to phone numbers format
      const transformedPhoneNumbers: PhoneNumber[] = accounts.map((account: any, index: number) => ({
        id: account.phoneNumberId || `phone_${index}`,
        phoneNumber: account.phoneNumber || `+${account.phoneNumberId}`,
        phoneNumberId: account.phoneNumberId,
        businessName: account.businessName || `Business ${account.businessId}`,
        status: account.status || 'active',
      }));

      setPhoneNumbers(transformedPhoneNumbers);
      
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch phone numbers');
    } finally {
      setPhoneNumbersLoading(false);
    }
  };

  const loadLeads = async () => {
    setLeadsLoading(true);
    try {
      const account = await loadAccount(displayPhoneNumber);
      const [people, pipeline, messages] = await Promise.all([
        getBusinessLeads({
          whatsappBusinessId: account.businessPhone,
          instagramBusinessId: account.instagramId,
        }),
        getLeadPipeline(businessId()).catch((): null => null),
        account.phoneNumberId
          ? getBusinessMessages(account.phoneNumberId).catch((): null => null)
          : Promise.resolve(null),
      ]);
      const times: Record<string, string> = {};
      (messages?.messages || []).forEach((msg: { customerNumber?: string; timestamp?: string; updatedAt?: string }) => {
        const key = phoneKey(String(msg.customerNumber || ""));
        const at = [msg.timestamp, msg.updatedAt].map((value) => String(value || "")).sort().pop() || "";
        if (!key || !at) return;
        if (!times[key] || at > times[key]) times[key] = at;
      });
      setReplyAt(times);
      setLeadRows(leadsToChats(people));
      setLeadSetup(() => {
        const saved = loadSetup();
        if (!pipeline) return saved;
        return { ...saved, stages: pipeline.stages, tags: pipeline.tags, fields: pipeline.fields };
      });
    } catch {
      setLeadRows([]);
    } finally {
      setLeadsLoading(false);
    }
  };

  useEffect(() => {
    if (wabaId) {
      fetchApprovedTemplates();
      fetchPhoneNumbers();
    }
    loadLeads();
  }, [wabaId, displayPhoneNumber]);

  // Handle template selection from dropdown
  const handleTemplateSelect = async (templateId: string) => {
    if (!templateId) {
      setSelectedTemplate(null);
      setSelectedTemplateId('');
      setVariables({});
      return;
    }
    
    setSelectedTemplateId(templateId);
    
    try {
      const fullTemplate = await fetchFullTemplateDetails(templateId);
      console.log('Full template details:', fullTemplate);
      setSelectedTemplate(fullTemplate);
      // Reset variables when template changes
      setVariables({});
    } catch (error) {
      console.error('Error fetching template details:', error);
      setError('Failed to load template details');
    }
  };

  // Extract variables from template content
  const templateVariables = selectedTemplate && selectedTemplate.content ? 
    (selectedTemplate.content.match(/\{\{([^}]+)\}\}/g) || []).map(v => v.replace(/\{\{|\}\}/g, '')) : 
    [];

  // Function to replace variables in template content
  const replaceTemplateVariables = (content: string) => {
    if (!content) return content;
    
    return content.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
      return variables[variable] || match;
    });
  };

  // Extract variable names from template example body_text
  const getTemplateVariableNames = (): string[] => {
    if (!selectedTemplate) return [];
    
    const bodyComponent = selectedTemplate.components?.find((c: any) => c.type === "BODY");
    if (!bodyComponent?.example?.body_text) return [];
    
    // body_text is an array of arrays, get the first array
    const variableNames = Array.isArray(bodyComponent.example.body_text[0]) 
      ? bodyComponent.example.body_text[0] 
      : bodyComponent.example.body_text;
    
    return Array.isArray(variableNames) ? variableNames : [];
  };

  // Create dynamic mapping from phone number columns to template variables
  const mapPhoneToTemplateVariables = (phone: any): Record<string, string> => {
    const variableNames = getTemplateVariableNames();
    const mapping: Record<string, string> = {};
    
    // Create mapping: {{1}} -> first variable name, {{2}} -> second, etc.
    variableNames.forEach((varName: string, index: number) => {
      const varNum = String(index + 1); // 1, 2, 3, etc.
      
      // Try to find matching column in phone object (case-insensitive)
      const phoneKey = Object.keys(phone).find(key => 
        key.toLowerCase() === varName.toLowerCase() ||
        key.toLowerCase().includes(varName.toLowerCase()) ||
        varName.toLowerCase().includes(key.toLowerCase())
      );
      
      if (phoneKey) {
        mapping[varNum] = String(phone[phoneKey] || '');
      } else {
        // Fallback: try common mappings
        if (varName.toLowerCase().includes('name')) {
          mapping[varNum] = String(phone.name || phone.contactName || '');
        } else if (varName.toLowerCase().includes('course')) {
          mapping[varNum] = String(phone.course || '');
        } else if (varName.toLowerCase().includes('date')) {
          mapping[varNum] = String(phone.date || phone.createdAt || '');
        } else if (varName.toLowerCase().includes('location') || varName.toLowerCase().includes('address')) {
          mapping[varNum] = String(phone.location || phone.address || '');
        } else if (varName.toLowerCase().includes('website') || varName.toLowerCase().includes('url')) {
          mapping[varNum] = String(phone.website || phone.url || '');
        } else if (varName.toLowerCase().includes('phone')) {
          mapping[varNum] = String(phone.phoneNumber || phone.phone || '');
        } else {
          mapping[varNum] = String(phone[varName] || phone[varName.toLowerCase()] || '');
        }
      }
    });
    
    // Always include header_url for image headers - prioritize phone's image over template example
    // Check multiple possible field names for image
    const phoneImage = phone.image || 
                      phone.imageUrl || 
                      phone.image_url ||
                      phone.avatar || 
                      phone.photo ||
                      phone.picture ||
                      '';
    
    // Use phone's image if available, otherwise use manually set variable, otherwise use template example
    mapping['header_url'] = phoneImage || variables['header_url'] || '';
    
    // Include any manually set variables
    return { ...mapping, ...variables };
  };

  // Check if a value is an image URL
  const isImageUrl = (value: any): boolean => {
    if (!value || typeof value !== 'string') return false;
    if (value === '--' || value.trim() === '') return false;
    
    // Check if it starts with http:// or https://
    const isHttpUrl = /^https?:\/\//i.test(value);
    if (!isHttpUrl) return false;
    
    // Check if it's a URL
    try {
      const url = new URL(value);
      // Check if it's an image URL by extension
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
      const hasImageExtension = imageExtensions.some(ext => 
        url.pathname.toLowerCase().endsWith(ext)
      );
      
      // Also check if URL contains 'image' in path
      const hasImageInPath = url.pathname.toLowerCase().includes('image') || 
                            url.pathname.toLowerCase().includes('/image/');
      
      return hasImageExtension || hasImageInPath;
    } catch {
      // If URL parsing fails, check if it looks like an image URL by pattern
      return /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/i.test(value) || 
             /\/image\//i.test(value);
    }
  };

  // Handle phone number selection
  const handlePhoneNumberToggle = (phoneNumberId: string) => {
    setSelectedPhoneNumbers(prev => 
      prev.includes(phoneNumberId) 
        ? prev.filter(id => id !== phoneNumberId)
        : [...prev, phoneNumberId]
    );
  };

  // Handle group selection
  const handleGroupToggle = (groupId: string) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Handle group phone number selection
  const handleGroupPhoneNumberToggle = (phoneNumberId: string) => {
    console.log('Toggling phone number:', phoneNumberId);
    console.log('Current selected:', selectedGroupPhoneNumbers);
    
    setSelectedGroupPhoneNumbers(prev => {
      const newSelection = prev.includes(phoneNumberId) 
        ? prev.filter(id => id !== phoneNumberId)
        : [...prev, phoneNumberId];
      
      console.log('New selection:', newSelection);
      return newSelection;
    });
  };

  // Handle select all/none
  const handleSelectAll = () => {
    if (selectedGroup) {
      if (selectedGroupPhoneNumbers.length === groupPhoneNumbers.length) {
        setSelectedGroupPhoneNumbers([]);
      } else {
        setSelectedGroupPhoneNumbers(groupPhoneNumbers.map((phone: any) => phone.id));
      }
    } else {
      if (selectedGroups.length === phoneGroups.length) {
        setSelectedGroups([]);
      } else {
        setSelectedGroups(phoneGroups.map((g: any) => g.id));
      }
    }
  };

  const fieldChoices = useMemo(() => leadSetup.fields.map((field) => {
    const values = new Set<string>();
    leadRows.forEach((chat) => {
      const value = (leadState(leadSetup, chat.key).attributes[field.id] || "").trim();
      if (value) values.add(value);
    });
    return { field, values: Array.from(values).sort((a, b) => a.localeCompare(b)) };
  }).filter((choice) => choice.values.length > 0), [leadRows, leadSetup]);

  const visibleRecipients = useMemo(() => {
    const needle = leadQuery.trim().toLowerCase();
    const byPhone = new Map<string, { phone: string; name: string; at: string; stage: string; tags: string[] }>();
    leadRows.forEach((chat) => {
      const lead = leadState(leadSetup, chat.key);
      const number = leadWhatsapp(leadSetup, chat);
      const haystack = `${chat.name} ${number}`.toLowerCase();
      if (needle && !haystack.includes(needle)) return;
      const fieldsMatch = Object.entries(fieldFilters).every(([fieldId, picked]) => {
        if (picked.length === 0) return true;
        return picked.includes((lead.attributes[fieldId] || "").trim());
      });
      if (!fieldsMatch) return;
      if (tagIds.length > 0 && !tagIds.some((id) => lead.tagIds.includes(id))) return;
      if (stageIds.length > 0 && !stageIds.includes(lead.stageId)) return;
      const phone = toE164(number);
      if (!phone || byPhone.has(phone)) return;
      byPhone.set(phone, {
        phone,
        name: chat.name || "Lead",
        at: replyAt[phoneKey(phone)] || "",
        stage: leadSetup.stages.find((stage) => stage.id === lead.stageId)?.name || "",
        tags: leadSetup.tags.filter((tag) => lead.tagIds.includes(tag.id)).map((tag) => tag.name),
      });
    });
    return Array.from(byPhone.values()).sort((a, b) => {
      const left = a.at ? new Date(a.at).getTime() : 0;
      const right = b.at ? new Date(b.at).getTime() : 0;
      return right - left;
    });
  }, [leadRows, leadSetup, leadQuery, fieldFilters, tagIds, stageIds, replyAt]);

  useEffect(() => {
    setPickedPhones(visibleRecipients.map((row) => row.phone));
  }, [visibleRecipients]);

  const picked = new Set(pickedPhones);
  const allChecked = visibleRecipients.length > 0 && visibleRecipients.every((row) => picked.has(row.phone));

  const filtersOn = tagIds.length > 0 || stageIds.length > 0 || Object.values(fieldFilters).some((picked) => picked.length > 0);

  const handleSendMarketing = async () => {
    if (!selectedTemplate) {
      setError("Select a template.");
      return;
    }
    if (pickedPhones.length === 0) {
      setError("Select at least one lead.");
      return;
    }
    if (scheduleMode === "later") {
      setError("Choose Send now. A later time is not saved from this page.");
      return;
    }
    const businessPhoneNumberId = phoneNumberId || sessionStorage.getItem("phoneNumberId") || "";
    const businessPhoneNumber = sessionStorage.getItem("display_phone_number") || localStorage.getItem("display_phone_number") || "";
    if (!businessPhoneNumberId || !businessPhoneNumber) {
      setError("The WhatsApp number for this business is missing. Log in again.");
      return;
    }

    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const components = buildWAComponentsForMarketing(variables, selectedTemplate);
      const preview = (selectedTemplate.content || selectedTemplate.name).replace(
        /\{\{\s*([^}]+)\s*\}\}/g,
        (_, key) => variables[String(key).trim()] || ""
      );
      let sent = 0;
      let failed = 0;
      for (const customerPhone of pickedPhones) {
        try {
          await sendTemplateMessage({
            customerPhone,
            businessPhoneNumberId,
            businessPhoneNumber,
            template_name: selectedTemplate.name,
            language_code: languageCode(selectedTemplate.language),
            category: selectedTemplate.category || "MARKETING",
            components,
            message: preview || selectedTemplate.name,
          });
          sent += 1;
        } catch {
          failed += 1;
        }
      }
      if (sent === 0) {
        setError("Could not send the template.");
      } else if (failed > 0) {
        setSuccess(`Template sent to ${sent} leads. ${failed} could not be sent.`);
      } else {
        setSuccess(`Template sent to ${sent} leads.`);
      }
    } catch (err: any) {
      setError(err?.message || "Could not send the template.");
    } finally {
      setSending(false);
    }
  };

  // Remove the early return for loading - let the UI render with overlay

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="mx-auto max-w-7xl px-6 py-8">
        {/* Page header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Send Marketing Messages</h1>
              <p className="text-lg text-gray-600">Choose a template, then filter leads by stage, tag, or other fields.</p>
              {wabaId && (
                <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                  <span className="mr-2">📱</span>
                  WABA ID: {wabaId}
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-6 py-4 text-red-700 shadow-sm">
            <div className="flex items-center">
              <span className="mr-3 text-xl">⚠️</span>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 rounded-xl border border-green-200 bg-green-50 px-6 py-4 text-green-700 shadow-sm">
            <div className="flex items-center">
              <span className="mr-3 text-xl">✅</span>
              <span className="font-medium">{success}</span>
            </div>
          </div>
        )}

        <div className={`grid gap-8 transition-all duration-300 ${rightPanelCollapsed ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
          {/* Left: Template Selection */}
          <div className={`rounded-2xl border border-gray-100 bg-white p-8 shadow-lg ${rightPanelCollapsed ? 'lg:col-span-1' : ''}`}>
            <h2 className="mb-6 text-xl font-semibold text-gray-900">1. Select Approved Template</h2>
            
            {/* Template Dropdown */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Template</label>
              {templatesLoading ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-gray-500">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>Loading templates...</span>
                  </div>
                </div>
              ) : (
                <select
                  value={selectedTemplateId}
                  onChange={(e) => handleTemplateSelect(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">— Select a template —</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name} - {template.category ? ` ${template.category}` : ""}
                    </option>
                  ))}
                </select>
              )}
              {templates.length === 0 && !templatesLoading && (
                <div className="mt-2 text-sm text-gray-500">
                  No approved templates found. Please create and approve templates first.
                </div>
              )}
            </div>

            {/* Template Preview - User-wise */}
            {selectedTemplate && (
              <div className="mt-6">
                <h3 className="mb-3 text-sm font-medium text-gray-700">Template Preview</h3>
                {selectedGroup && groupPhoneNumbers.length > 0 ? (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="mb-3 text-xs text-gray-600">
                      Showing {groupPhoneNumbers.filter(p => selectedGroupPhoneNumbers.includes(p.id)).length} preview{groupPhoneNumbers.filter(p => selectedGroupPhoneNumbers.includes(p.id)).length !== 1 ? 's' : ''} for selected recipients
                    </div>
                    <div className="grid grid-cols-1 gap-3 max-h-[500px] overflow-y-auto">
                      {groupPhoneNumbers
                        .filter(phone => selectedGroupPhoneNumbers.includes(phone.id))
                        .map((phone) => {
                          // Use dynamic mapping based on template example body_text
                          const recipientVariables = mapPhoneToTemplateVariables(phone);

                          // Replace template variables {{1}}, {{2}}, etc.
                          const bodyText = selectedTemplate.components?.find((c: any) => c.type === "BODY")?.text || selectedTemplate.content || "";
                          const replacedText = bodyText.replace(/\{\{(\d+)\}\}/g, (match: string, num: string) => {
                            return recipientVariables[num] || match;
                          });

                          const headerComponent = selectedTemplate.components?.find((c: any) => c.type === "HEADER" && c.format === "IMAGE");
                          const headerTextComponent = selectedTemplate.components?.find((c: any) => c.type === "HEADER" && c.format === "TEXT");
                          const footerComponent = selectedTemplate.components?.find((c: any) => c.type === "FOOTER");
                          const buttonsComponent = selectedTemplate.components?.find((c: any) => c.type === "BUTTONS");
                          
                          // Prioritize phone's image over template example image
                          // First, try direct field names (case-insensitive search)
                          let phoneImage = '';
                          
                          // Check all possible image field names
                          const imageFieldNames = ['image', 'imageUrl', 'image_url', 'Image', 'ImageUrl', 'IMAGE', 'avatar', 'photo', 'picture'];
                          for (const fieldName of imageFieldNames) {
                            if (phone[fieldName] && typeof phone[fieldName] === 'string' && phone[fieldName].trim() !== '') {
                              phoneImage = phone[fieldName].trim();
                              break;
                            }
                          }
                          
                          // If not found, search for any field containing "image" (case-insensitive)
                          if (!phoneImage) {
                            const imageKey = Object.keys(phone).find(key => 
                              key.toLowerCase().includes('image') && 
                              phone[key] && 
                              typeof phone[key] === 'string' &&
                              String(phone[key]).trim() !== '' &&
                              String(phone[key]).trim() !== '--' &&
                              (String(phone[key]).startsWith('http://') || String(phone[key]).startsWith('https://'))
                            );
                            if (imageKey) {
                              phoneImage = String(phone[imageKey]).trim();
                            }
                          }
                          
                          // Final validation - ensure it's a valid URL
                          if (phoneImage && !phoneImage.startsWith('http://') && !phoneImage.startsWith('https://')) {
                            phoneImage = '';
                          }
                          
                          console.log('Phone image check:', {
                            phoneId: phone.id,
                            phoneName: phone.name,
                            phoneImage: phoneImage || 'NOT FOUND',
                            hasImage: !!phoneImage,
                            allKeys: Object.keys(phone),
                            imageFields: Object.keys(phone).filter(k => {
                              const val = phone[k];
                              return k.toLowerCase().includes('image') && val && typeof val === 'string' && val.trim() !== '';
                            })
                          });
                          
                          // Use phone's image first, then manually set variable, then template example

                          return (
                            <div key={phone.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* WhatsApp-style message bubble */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                  {/* Header Image - Only show if template has image header component */}
                                  {headerComponent && (() => {
                                    // Prioritize phoneImage - use it if it's a valid URL
                                    const hasPhoneImage = phoneImage && phoneImage.trim() !== '' && 
                                                         (phoneImage.startsWith('http://') || phoneImage.startsWith('https://'));
                                    
                                    // Use phone's image if available, otherwise fall back to template example
                                    const imageToUse = hasPhoneImage 
                                      ? phoneImage 
                                      : (headerComponent?.example?.header_handle?.[0] || '');
                                    
                                    if (!imageToUse) {
                                      return null;
                                    }
                                    
                                    // Add cache-busting parameter ONLY for phone images to force reload
                                    // Use imageVersion + phone ID hash to ensure all images reload when phone numbers are fetched
                                    const imageUrlHash = imageToUse.split('/').pop()?.split('?')[0] || '';
                                    // Create a stable but unique cache buster based on phone ID and image URL
                                 
                                 
                                    
                                    // Create a unique key that includes phone ID, image hash, version, and random number
                                    // Random number ensures each image in the loop gets a completely unique key
                                    const randomNum = Math.random().toString(36).substring(2, 9);
                                    const fullImageKey = `${phone.id}-${imageUrlHash}-v${phone.id}-${randomNum}`;
                                    const imageKey = `img-${fullImageKey.substring(0, 120)}`;
                                    
                                    return (
                                      <div className="w-full h-24" key={`wrapper-${imageKey}`}>
                                        <img 
                                          key={imageKey}
                                          src={imageToUse}
                                        alt="Template header"
                                        className="w-full h-full object-cover"
                                          loading="eager"
                                          decoding="async"
                                      />
                                    </div>
                                    );
                                  })()}
                                  
                                  {/* Header Text */}
                                  {headerTextComponent?.text && (
                                    <div className="px-2 pt-2">
                                      <div className="text-xs font-semibold text-gray-900">
                                        {headerTextComponent.text}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Body Text */}
                                  <div className="p-2">
                                    <div className="text-xs text-gray-800 whitespace-pre-wrap break-words">
                                      {replacedText}
                                    </div>
                                  </div>
                                  
                                  {/* Buttons - Dynamic rendering based on template */}
                                  {buttonsComponent?.buttons && Array.isArray(buttonsComponent.buttons) && buttonsComponent.buttons.length > 0 && (
                                    <div className="px-2 pb-2 space-y-1">
                                      {buttonsComponent.buttons.map((button: any, btnIdx: number) => {
                                        // QUICK_REPLY buttons
                                        if (button.type === 'QUICK_REPLY') {
                                          return (
                                            <div
                                              key={btnIdx}
                                              className="text-xs px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 font-medium text-center"
                                            >
                                              {button.text}
                                            </div>
                                          );
                                        }
                                        // URL buttons
                                        else if (button.type === 'URL') {
                                          return (
                                            <div
                                              key={btnIdx}
                                              className="text-xs px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-green-700 font-medium text-center"
                                            >
                                              {button.text} 🔗
                                            </div>
                                          );
                                        }
                                        // PHONE_NUMBER buttons
                                        else if (button.type === 'PHONE_NUMBER') {
                                          return (
                                            <div
                                              key={btnIdx}
                                              className="text-xs px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-purple-700 font-medium text-center"
                                            >
                                              {button.text} 📞
                                            </div>
                                          );
                                        }
                                        // FLOW buttons
                                        else if (button.type === 'FLOW') {
                                          return (
                                            <div
                                              key={btnIdx}
                                              className="text-xs px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 font-medium text-center"
                                            >
                                              {button.text} 🔄
                                              {button.flow_id && (
                                                <div className="text-xs text-orange-600 mt-1">
                                                  Flow ID: {button.flow_id}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        }
                                        // Fallback for any other button types
                                        return (
                                          <div
                                            key={btnIdx}
                                            className="text-xs px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium text-center"
                                          >
                                            {button.text || `Button ${btnIdx + 1}`}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  
                                  {/* Footer */}
                                  {footerComponent?.text && (
                                    <div className="px-2 pb-2">
                                      <div className="text-xs text-gray-500 italic">
                                        {footerComponent.text}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-3 max-w-sm mx-auto">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800 mb-3">
                      This template goes to the leads that match the filters.
                    </div>
                    {/* Default preview with manual variables */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      {/* Header Image */}
                    {selectedTemplate?.components?.some((c: any) => c.type === "HEADER" && c.format === "IMAGE") && (
                      <div className="w-full">
                        <img 
                          src={variables['header_url'] || selectedTemplate.components.find((c: any) => c.type === "HEADER" && c.format === "IMAGE")?.example?.header_handle?.[0]}
                          alt="Template header"
                          className="w-full h-32 object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                      
                      {/* Header Text */}
                      {selectedTemplate.components?.find((c: any) => c.type === "HEADER" && c.format === "TEXT")?.text && (
                        <div className="px-3 pt-3">
                          <div className="text-sm font-semibold text-gray-900">
                            {selectedTemplate.components.find((c: any) => c.type === "HEADER" && c.format === "TEXT")?.text}
                          </div>
                      </div>
                    )}
                    
                    {/* Body Text */}
                    <div className="p-3">
                      <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                          {(selectedTemplate.components?.find((c: any) => c.type === "BODY")?.text || selectedTemplate.content || "").replace(/\{\{(\d+)\}\}/g, (match: string, num: string) => {
                            return variables[num] || match;
                          })}
                      </div>
                    </div>
                      
                      {/* Buttons - Dynamic rendering based on template */}
                      {selectedTemplate.components?.find((c: any) => c.type === "BUTTONS")?.buttons && Array.isArray(selectedTemplate.components.find((c: any) => c.type === "BUTTONS")?.buttons) && (
                        <div className="px-3 pb-3 space-y-2">
                          {selectedTemplate.components.find((c: any) => c.type === "BUTTONS")?.buttons.map((button: any, btnIdx: number) => {
                            // QUICK_REPLY buttons
                            if (button.type === 'QUICK_REPLY') {
                              return (
                                <div
                                  key={btnIdx}
                                  className="text-xs px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 font-medium text-center"
                                >
                                  {button.text}
                                </div>
                              );
                            }
                            // URL buttons
                            else if (button.type === 'URL') {
                              return (
                                <div
                                  key={btnIdx}
                                  className="text-xs px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-green-700 font-medium text-center"
                                >
                                  {button.text} 🔗
                                </div>
                              );
                            }
                            // PHONE_NUMBER buttons
                            else if (button.type === 'PHONE_NUMBER') {
                              return (
                                <div
                                  key={btnIdx}
                                  className="text-xs px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-purple-700 font-medium text-center"
                                >
                                  {button.text} 📞
                                </div>
                              );
                            }
                            // FLOW buttons
                            else if (button.type === 'FLOW') {
                              return (
                                <div
                                  key={btnIdx}
                                  className="text-xs px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 font-medium text-center"
                                >
                                  {button.text} 🔄
                                  {button.flow_id && (
                                    <div className="text-xs text-orange-600 mt-1">
                                      Flow ID: {button.flow_id}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            // Fallback for any other button types
                            return (
                              <div
                                key={btnIdx}
                                className="text-xs px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium text-center"
                              >
                                {button.text || `Button ${btnIdx + 1}`}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    
                    {/* Footer */}
                      {selectedTemplate.components?.find((c: any) => c.type === "FOOTER") && (
                      <div className="px-3 pb-3">
                        <div className="text-xs text-gray-500 border-t pt-2">
                            {selectedTemplate.components.find((c: any) => c.type === "FOOTER")?.text}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>
            )}
          </div>

          {/* Right: Phone Numbers & Groups Selection */}
          {!rightPanelCollapsed && (
          <div className="rounded-2xl border border-gray-100 bg-white p-8 shadow-lg">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">2. Filter leads</h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setRecipientsCollapsed(!recipientsCollapsed)}
                    className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900"
                    title={recipientsCollapsed ? "Expand" : "Collapse"}
                  >
                    {recipientsCollapsed ? (
                      <>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                        <span>Expand</span>
                      </>
                    ) : (
                      <>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                        <span>Collapse</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setRightPanelCollapsed(true)}
                    className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900"
                    title="Close panel"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            
            {!recipientsCollapsed ? (
              <div>
                <input
                  value={leadQuery}
                  onChange={(event) => setLeadQuery(event.target.value)}
                  placeholder="Search leads"
                  className="mb-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
                {leadSetup.stages.length > 0 ? (
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">Stage</span>
                    {leadSetup.stages.map((stage) => {
                      const on = stageIds.includes(stage.id);
                      return (
                        <button
                          key={stage.id}
                          type="button"
                          onClick={() => setStageIds(toggleId(stageIds, stage.id))}
                          className={`rounded-full px-3 py-1 text-xs font-semibold text-white ${on ? "ring-2 ring-gray-900 ring-offset-1" : stageIds.length ? "opacity-50" : ""}`}
                          style={{ background: stage.color || "#2563eb" }}
                        >
                          {stage.name}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                {leadSetup.tags.length > 0 ? (
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">Tags</span>
                    {leadSetup.tags.map((tag) => {
                      const on = tagIds.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => setTagIds(toggleId(tagIds, tag.id))}
                          className={`rounded-full px-3 py-1 text-xs font-semibold text-white ${on ? "ring-2 ring-gray-900 ring-offset-1" : tagIds.length ? "opacity-50" : ""}`}
                          style={{ background: tag.color || "#2563eb" }}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
                {fieldChoices.map(({ field, values }) => {
                  const picked = fieldFilters[field.id] || [];
                  return (
                    <div key={field.id} className="mb-3 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-gray-500">{field.name}</span>
                      {values.map((value) => {
                        const on = picked.includes(value);
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setFieldFilters({ ...fieldFilters, [field.id]: toggleId(picked, value) })}
                            className={`rounded-full px-3 py-1 text-xs font-medium ${on ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-600"}`}
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
                {filtersOn ? (
                  <button
                    type="button"
                    onClick={() => {
                      setFieldFilters({});
                      setTagIds([]);
                      setStageIds([]);
                    }}
                    className="mb-3 text-xs font-medium text-blue-600"
                  >
                    Clear filters
                  </button>
                ) : null}
                {leadsLoading ? (
                  <p className="text-sm text-gray-500">Loading leads...</p>
                ) : visibleRecipients.length === 0 ? (
                  <p className="text-sm text-gray-500">No leads with a WhatsApp number match these filters.</p>
                ) : (
                  <div>
                    <label className="mb-2 flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={() => setPickedPhones(allChecked ? [] : visibleRecipients.map((row) => row.phone))}
                      />
                      {pickedPhones.length} of {visibleRecipients.length} selected
                    </label>
                    <div className="mb-1 grid grid-cols-[auto_1fr_auto] gap-3 px-1 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                      <span />
                      <span>Name and number</span>
                      <span>Last reply</span>
                    </div>
                    <div className="max-h-80 space-y-1 overflow-y-auto">
                      {visibleRecipients.map((row) => (
                        <label key={row.phone} className="grid cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-3 rounded-lg border border-gray-100 px-2 py-2 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={picked.has(row.phone)}
                            onChange={() => setPickedPhones((current) => (
                              current.includes(row.phone) ? current.filter((phone) => phone !== row.phone) : current.concat(row.phone)
                            ))}
                          />
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-medium text-gray-900">{row.name}</span>
                            <span className="block truncate text-xs text-gray-500">{row.phone}</span>
                            {row.stage || row.tags.length > 0 ? (
                              <span className="mt-1 flex flex-wrap gap-1">
                                {row.stage ? <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-700">{row.stage}</span> : null}
                                {row.tags.map((tag) => (
                                  <span key={tag} className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700">{tag}</span>
                                ))}
                              </span>
                            ) : null}
                          </span>
                          <span className="text-right text-xs text-gray-500">{replyLabel(row.at)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-600">{pickedPhones.length} of {visibleRecipients.length} leads selected</p>
            )}
          </div>
        )}

          {/* Collapsed right panel - show expand button */}
          {rightPanelCollapsed && (
            <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-10">
              <button
                onClick={() => setRightPanelCollapsed(false)}
                className="bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
                title="Show recipients panel"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
          </div>
        )}
        </div>

        {/* Scheduling Options */}
        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold">3. Scheduling</h2>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="scheduleMode"
                  value="now"
                  checked={scheduleMode === 'now'}
                  onChange={(e) => setScheduleMode('now')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Send Now</span>
              </label>
              
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="scheduleMode"
                  value="later"
                  checked={scheduleMode === 'later'}
                  onChange={(e) => setScheduleMode('later')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Schedule for Later</span>
              </label>
            </div>

            {scheduleMode === 'later' && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scheduled Time
                </label>
                <input
                  type="datetime-local"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none w-full"
                  required={scheduleMode === 'later'}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-white p-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">
              {selectedTemplate ? selectedTemplate.name : "No template selected"}
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {stageIds.length === 0 && tagIds.length === 0 && !Object.values(fieldFilters).some((picked) => picked.length > 0) ? (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">All leads</span>
              ) : null}
              {leadSetup.stages.filter((stage) => stageIds.includes(stage.id)).map((stage) => (
                <span key={stage.id} className="rounded-full px-2 py-0.5 text-xs font-medium text-white" style={{ background: stage.color || "#2563eb" }}>
                  {stage.name}
                </span>
              ))}
              {leadSetup.tags.filter((tag) => tagIds.includes(tag.id)).map((tag) => (
                <span key={tag.id} className="rounded-full px-2 py-0.5 text-xs font-medium text-white" style={{ background: tag.color || "#2563eb" }}>
                  {tag.name}
                </span>
              ))}
              {fieldChoices.flatMap(({ field, values }) => (
                (fieldFilters[field.id] || []).filter((value) => values.includes(value)).map((value) => (
                  <span key={`${field.id}-${value}`} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                    {field.name}: {value}
                  </span>
                ))
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">{pickedPhones.length} leads will get this message</p>
          </div>
          <button
            onClick={handleSendMarketing}
            disabled={!selectedTemplate || pickedPhones.length === 0 || sending || scheduleMode === "later"}
            className={`rounded-lg px-6 py-3 font-medium text-white transition ${
              !selectedTemplate || pickedPhones.length === 0 || sending || scheduleMode === "later"
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {sending ? "Sending..." : `Send to ${pickedPhones.length} leads`}
          </button>
        </div>
      </div>

      {/* Media Selection Popup */}
      <MediaSelectionPopup
        isOpen={showMediaPopup}
        onClose={() => setShowMediaPopup(false)}
        onSelect={handleMediaSelect}
        title="Select Header Image"
      />

      {(templatesLoading || leadsLoading) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <LoadingComponent 
              loading={true}
              message="Loading marketing data..."
              showRefreshButton={false}
              size="large"
            />
          </div>
        </div>
      )}
    </div>
  );
}

